/**
 * API client.
 *
 * Deliberately mirrors the shape the Base44 SDK exposed — `api.entities.X.list()`,
 * `api.functions.invoke()` — so the pages ported from the old app keep their
 * existing call sites and TanStack Query keys. The transport underneath is our
 * own REST API with session cookies; nothing of Base44 remains but the ergonomics.
 */

/** Thrown for any non-2xx response. `details` carries per-field validation messages. */
export class ApiError extends Error {
  constructor(message, status, details) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

async function request(path, { method = 'GET', body, signal } = {}) {
  const response = await fetch(`/api${path}`, {
    method,
    headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
    // Session cookie travels with every call; the API is same-origin.
    credentials: 'same-origin',
    signal,
  });

  if (response.status === 204) return null;

  const text = await response.text();
  let payload = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      // A non-JSON body from a proxy or crash — surface the status, not a parse error.
      throw new ApiError(`Unexpected response from server (${response.status})`, response.status);
    }
  }

  if (!response.ok) {
    throw new ApiError(
      payload?.error ?? `Request failed (${response.status})`,
      response.status,
      payload?.details,
    );
  }

  return payload;
}

/**
 * Standard CRUD surface for one collection.
 *
 * `list()` accepts a sort argument for call-site compatibility with the old SDK,
 * but ignores it: ordering is decided server-side, where it can use an index.
 * The parameter is kept so ported code reads unchanged rather than sprouting a
 * diff on every list call.
 */
function collection(basePath) {
  return {
    list: (_sort) => request(basePath),
    filter: (query) => {
      const params = new URLSearchParams(query).toString();
      return request(params ? `${basePath}?${params}` : basePath);
    },
    get: (id) => request(`${basePath}/${encodeURIComponent(id)}`),
    create: (data) => request(basePath, { method: 'POST', body: data }),
    update: (id, data) =>
      request(`${basePath}/${encodeURIComponent(id)}`, { method: 'PATCH', body: data }),
    delete: (id) => request(`${basePath}/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  };
}

export const api = {
  request,

  entities: {
    ShippingBox: collection('/boxes'),
    Media: collection('/media'),
    ReturnAddress: collection('/return-addresses'),
    DeliveryAddress: collection('/delivery-addresses'),

    /** Singleton, so it reads and writes the whole record rather than by id. */
    AppSettings: {
      get: () => request('/settings'),
      save: (data) => request('/settings', { method: 'PUT', body: data }),
    },
  },

  users: {
    list: () => request('/users'),
    create: (data) => request('/users', { method: 'POST', body: data }),
    update: (id, data) => request(`/users/${encodeURIComponent(id)}`, { method: 'PATCH', body: data }),
    setPassword: (id, newPassword) =>
      request(`/users/${encodeURIComponent(id)}/password`, { method: 'POST', body: { newPassword } }),
    ban: (id, reason) =>
      request(`/users/${encodeURIComponent(id)}/ban`, { method: 'POST', body: { reason } }),
    unban: (id) => request(`/users/${encodeURIComponent(id)}/unban`, { method: 'POST' }),
  },

  /**
   * Printing.
   *
   * `status` is what every page needs before offering a Print button; the rest
   * is setup and is refused for non-admins by the server.
   */
  printing: {
    status: () => request('/print/status'),
    print: (data) => request('/print', { method: 'POST', body: data }),
    job: (id) => request(`/print/jobs/${encodeURIComponent(id)}`),
    cancelJob: (id) => request(`/print/jobs/${encodeURIComponent(id)}/cancel`, { method: 'POST' }),

    listAgents: () => request('/print/agents'),
    createAgent: (data) => request('/print/agents', { method: 'POST', body: data }),
    rotateAgentToken: (id) =>
      request(`/print/agents/${encodeURIComponent(id)}/rotate-token`, { method: 'POST' }),
    deleteAgent: (id) => request(`/print/agents/${encodeURIComponent(id)}`, { method: 'DELETE' }),

    listPrinters: () => request('/print/printers'),
    createPrinter: (data) => request('/print/printers', { method: 'POST', body: data }),
    updatePrinter: (id, data) =>
      request(`/print/printers/${encodeURIComponent(id)}`, { method: 'PATCH', body: data }),
    deletePrinter: (id) =>
      request(`/print/printers/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  },

  me: () => request('/me'),
  version: () => request('/version'),
};
